import React, { useState, useEffect } from "react";
import { getWebinarsAdmin, createWebinar, updateWebinar, deleteWebinar } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const WebinarsManager = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [formData, setFormData] = useState({
    en: { topic: "", speaker: "", curator: "", abstract: "", recording_date_text: "", publication_date_text: "" },
    vi: { topic: "", speaker: "", curator: "", abstract: "", recording_date_text: "", publication_date_text: "" },
    link_url: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    setLoading(true);
    try {
      const data = await getWebinarsAdmin();
      setWebinars(data);
    } catch (err) {
      setToast({ message: "Failed to load webinars", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        link_url: formData.link_url,
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updateWebinar(editingId, payload);
      } else {
        await createWebinar(payload);
      }
      fetchWebinars();
      resetForm();
      setToast({ message: "Webinar saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save webinar", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this webinar?")) {
      try {
        await deleteWebinar(id);
        fetchWebinars();
        setToast({ message: "Webinar deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete webinar", type: "error" });
      }
    }
  };

  const handleEdit = (webinar) => {
    setFormData({
      en: {
        topic: webinar.translations.en?.topic || "",
        speaker: webinar.translations.en?.speaker || "",
        curator: webinar.translations.en?.curator || "",
        abstract: webinar.translations.en?.abstract || "",
        recording_date_text: webinar.translations.en?.recording_date_text || "",
        publication_date_text: webinar.translations.en?.publication_date_text || "",
      },
      vi: {
        topic: webinar.translations.vi?.topic || "",
        speaker: webinar.translations.vi?.speaker || "",
        curator: webinar.translations.vi?.curator || "",
        abstract: webinar.translations.vi?.abstract || "",
        recording_date_text: webinar.translations.vi?.recording_date_text || "",
        publication_date_text: webinar.translations.vi?.publication_date_text || "",
      },
      link_url: webinar.link_url || "",
      sort_order: webinar.sort_order,
      is_active: webinar.is_active,
    });
    setEditingId(webinar.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { topic: "", speaker: "", curator: "", abstract: "", recording_date_text: "", publication_date_text: "" },
      vi: { topic: "", speaker: "", curator: "", abstract: "", recording_date_text: "", publication_date_text: "" },
      link_url: "",
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
  };

  if (loading) return <div className="admin-manager-loading">Loading webinars...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Webinar"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Webinar" : "Create New Webinar"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Topic (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.topic}
                    onChange={(e) => handleInputChange("en", "topic", e.target.value)}
                    placeholder="English topic"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Speaker (EN)</label>
                  <input
                    type="text"
                    value={formData.en.speaker}
                    onChange={(e) => handleInputChange("en", "speaker", e.target.value)}
                    placeholder="Speaker name"
                  />
                </div>
                <div className="form-group">
                  <label>Curator (EN)</label>
                  <input
                    type="text"
                    value={formData.en.curator}
                    onChange={(e) => handleInputChange("en", "curator", e.target.value)}
                    placeholder="Curator name"
                  />
                </div>
                <div className="form-group">
                  <label>Abstract (EN)</label>
                  <textarea
                    value={formData.en.abstract}
                    onChange={(e) => handleInputChange("en", "abstract", e.target.value)}
                    placeholder="English abstract"
                    rows="5"
                  />
                </div>
                <div className="form-group">
                  <label>Recording Date Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.recording_date_text}
                    onChange={(e) => handleInputChange("en", "recording_date_text", e.target.value)}
                    placeholder="e.g., Jan 01, 2024"
                  />
                </div>
                <div className="form-group">
                  <label>Publication Date Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.publication_date_text}
                    onChange={(e) => handleInputChange("en", "publication_date_text", e.target.value)}
                    placeholder="e.g., Jan 15, 2024"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Topic (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.topic}
                    onChange={(e) => handleInputChange("vi", "topic", e.target.value)}
                    placeholder="Vietnamese topic"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Speaker (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.speaker}
                    onChange={(e) => handleInputChange("vi", "speaker", e.target.value)}
                    placeholder="Tên người trình bày"
                  />
                </div>
                <div className="form-group">
                  <label>Curator (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.curator}
                    onChange={(e) => handleInputChange("vi", "curator", e.target.value)}
                    placeholder="Tên người quản lý"
                  />
                </div>
                <div className="form-group">
                  <label>Abstract (VI)</label>
                  <textarea
                    value={formData.vi.abstract}
                    onChange={(e) => handleInputChange("vi", "abstract", e.target.value)}
                    placeholder="Vietnamese abstract"
                    rows="5"
                  />
                </div>
                <div className="form-group">
                  <label>Recording Date Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.recording_date_text}
                    onChange={(e) => handleInputChange("vi", "recording_date_text", e.target.value)}
                    placeholder="e.g., 01/01/2024"
                  />
                </div>
                <div className="form-group">
                  <label>Publication Date Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.publication_date_text}
                    onChange={(e) => handleInputChange("vi", "publication_date_text", e.target.value)}
                    placeholder="e.g., 15/01/2024"
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h4>General Information</h4>
              <div className="form-group">
                <label>Link URL</label>
                <input
                  type="text"
                  value={formData.link_url}
                  onChange={(e) => handleMetaChange("link_url", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Webinar
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Topic (EN)</th>
              <th>Speaker</th>
              <th>Recording Date</th>
              <th>Publication Date</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {webinars.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                  No webinars found
                </td>
              </tr>
            ) : (
              webinars.map((webinar, index) => (
                <tr key={webinar.id}>
                  <td>{index + 1}</td>
                  <td>{webinar.translations.en?.topic || "N/A"}</td>
                  <td>{webinar.translations.en?.speaker || "N/A"}</td>
                  <td>{webinar.translations.en?.recording_date_text || "N/A"}</td>
                  <td>{webinar.translations.en?.publication_date_text || "N/A"}</td>
                  <td>{webinar.is_active ? "Yes" : "No"}</td>
                  <td>{webinar.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(webinar)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(webinar.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WebinarsManager;
